import { Vector3 } from "three";
import { hasOwnProperty, isDefined } from "../../infra3d";
import { Axis } from "../linearreference/internal";
import { FeatureCollection, FeatureStyle, GEOMETRYTYPE } from "../featuretree/internal";
import { MEASUREMODE } from "../internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { EventEmitter } from "../utils/EventEmitter";
/**
 * Abstract tools class
 * @typedef {Object} mmTool
 * @class Tool
 * @constructor
 */
export class Tool extends EventEmitter {
    constructor(scene, toolhandler) {
        super();
        this.scene = null;
        this.engine = null;
        this.enabled = false;
        this.toolhandler = null;
        this.options = {};
        this.selectedFeature = null;
        this.selectedStart = null;
        this.featureoffset = 0;
        this.nearestPoint = -1;
        this.scene = scene;
        this.engine = scene.engine;
        this.toolhandler = toolhandler;
        this.options.cbobject = this;
        this.options.singleactive = true;
        this.options.measuremodes = [MEASUREMODE.MONOPLOTTING];
        this.setOptions(this.options);
        this.orthoCollection = new FeatureCollection(scene, this.options.basestyle, this.options.basestyle, "orthcoCollection", false, scene.getFeatureHandler());
    }
    setOptions(options) {
        const layerstyle = this.toolhandler.defaultstyles.standard;
        const highlightstyle = new FeatureStyle({
            stylename: "DrawStyle_h",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        "font-marker": {
                            name: "circle-o",
                        },
                        size: 10,
                        stroke: [1, 1, 0, 0.5],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 2,
                        stroke: [1, 1, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "polygon",
                    params: {
                        fill: [1, 1, 0, 0.4],
                    },
                },
            ],
        });
        const basestyle = new FeatureStyle({
            stylename: "BaseDrawStyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 4,
                        stroke: [1, 0, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 2,
                        stroke: [0, 1, 0, 0.8],
                    },
                },
            ],
        });
        const defaultoptions = {
            layerstyle: layerstyle,
            highlightlayerstyle: highlightstyle,
            basestyle: basestyle,
            finishedcbf: null,
            errorcbf: null,
            enablecbf: null,
            disablecbf: null,
            finishinput: USERINPUTTYPE.RIGHTCLICK,
            resetinput: USERINPUTTYPE.KEY_ESC,
            resetcbf: null,
            activecursor: "crosshair",
            modifycursor: true,
            continousdraw: true,
            continousdrawcbf: null,
            maxdist: 20,
            snaplayer: this.toolhandler.snaplayers,
            uniqueactive: false,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        this.parseOptions(options);
    }
    /**
     * parses the options object
     * @method parseOptions
     * @param {Object} options
     */
    parseOptions(options) {
        for (const property in options) {
            if (hasOwnProperty(this.options, property)) {
                this.options[property] = options[property];
            }
        }
    }
    /**
     * disables all active tools and enables this tool
     * @method enable
     */
    enable(feature) {
        if (isDefined(feature)) {
            if (feature !== null)
                this.selectedFeature = feature;
        }
        if (this.options.singleactive)
            this.toolhandler.disableAllTools();
        if (this.options.uniqueactive) {
            this.toolhandler.disableAllToolsOfType(this.getType());
        }
        this._enableTool();
        this.enabled = true;
    }
    /**
     * disables this tool
     * @method disable
     */
    disable() {
        this.removeOrthoBase();
        if (!this.enabled)
            return; //if tool is not active there is nothing to disable...
        this._disableTool();
        this.enabled = false;
    }
    removeOrthoBase() {
        if (this.orthoBase != null)
            this.orthoCollection.destroyFeature(this.orthoBase);
        this.orthoBase = null;
        this.selectedFeature = null;
        this.featureoffset = 0;
    }
    findOrthogonal2(pos) {
        this.selectedStart = null;
        const axis_fc = {
            type: "FeatureCollection",
            features: [this.selectedFeature.toGeoJSON()],
        };
        const axis = new Axis(axis_fc, undefined, undefined, undefined, "name");
        const lrcoords = axis.xyz2uvw(pos.Easting, pos.Northing, pos.Height);
        if (lrcoords) {
            const orthopos = pos.copy();
            orthopos.Easting = lrcoords.axpoint[0];
            orthopos.Northing = lrcoords.axpoint[1];
            this.selectedStart = orthopos;
        }
    }
    /**
     * finds the nearest point on the feature between pos and the feature
     * @param pos
     */
    findOrthogonal(pos) {
        this.selectedStart = null;
        const selectedGeom = this.selectedFeature.geometry;
        let startIdx = 0;
        let endIdx = 0;
        if (selectedGeom.type == GEOMETRYTYPE.LINESTRING || selectedGeom.type == GEOMETRYTYPE.POLYGON) {
            let is2dfeature = false;
            let coordinates = selectedGeom.coordinates;
            if (selectedGeom.coordinates[0].length == 2) {
                is2dfeature = true;
                coordinates = selectedGeom.node.coordinates;
            }
            const orthLines = this._findRefLines(coordinates, pos, selectedGeom.type, is2dfeature);
            this.featureoffset %= orthLines.length;
            if (this.nearestPoint + this.featureoffset >= orthLines.length) {
                startIdx = orthLines.length - this.nearestPoint - this.featureoffset;
            }
            else if (this.nearestPoint + this.featureoffset < 0) {
                startIdx = orthLines.length + this.nearestPoint + this.featureoffset;
            }
            else {
                startIdx = this.nearestPoint + this.featureoffset;
            }
            endIdx = selectedGeom.type == GEOMETRYTYPE.POLYGON && startIdx == orthLines.length - 1 ? 0 : startIdx + 1;
            this.selectedStart = orthLines[startIdx];
            if (is2dfeature)
                pos.Height = this.selectedStart.Height;
            const jsonString = "{" +
                '"type":"Feature",' +
                '"geometry":{' +
                '"type":"LineString",' +
                '"coordinates":[' +
                "[" +
                selectedGeom.coordinates[startIdx].toString() +
                "]," +
                "[" +
                selectedGeom.coordinates[endIdx].toString() +
                "]" +
                "]" +
                "}" +
                "}";
            if (this.orthoBase != null)
                this.orthoCollection.destroyFeature(this.orthoBase);
            this.orthoBase = this.orthoCollection.createFeature();
            this.orthoBase.parseFromJSON(JSON.parse(jsonString));
        }
    }
    /**
     * finds all closest points on all lines of the feature
     *
     * @param points basepoints of the feature
     * @param pos position from which nearest point is searched
     * @param type GEOMETRYTYPE.POLYGON needs one more line to close the circle
     * @param is2dfeature boolean
     * @returns {MeasurePoint[]} all nearest points
     * @private
     */
    _findRefLines(points, pos, type, is2dfeature = false) {
        const v3Points = [];
        const v3Lines = [];
        const orthogonals = [];
        let minDistanceOnLine = Infinity;
        let minDistanceOffLine = Infinity;
        points.forEach(function (point) {
            v3Points.push(new Vector3().fromArray(point));
        });
        for (let i = 1; i < v3Points.length; i++) {
            v3Lines[i - 1] = new Vector3().subVectors(v3Points[i], v3Points[i - 1]);
        }
        if (type == GEOMETRYTYPE.POLYGON) {
            v3Lines[v3Lines.length] = new Vector3().subVectors(v3Points[0], v3Points[v3Lines.length]);
        }
        for (let i = 0; i < v3Lines.length; i++) {
            /**
         *     P
                 /|
                / |
            /  v
            A---X----->D
            * A: starting point
            * P: position from which nearest point is searched
            * D: normalized vector of the featureline
            * X: closest point
            */
            const A = v3Points[i].clone();
            const P = pos.getVec3().clone();
            const D = v3Lines[i].clone().normalize();
            const AP = new Vector3().subVectors(P, A);
            const scalar = AP.dot(D);
            const pointX = D.multiplyScalar(scalar);
            const X = new Vector3().addVectors(A, pointX);
            /**
             * find nearest point that is still in its segment
             * if non is in it segment choose the nearest possible point
             */
            const PX = new Vector3().subVectors(X, P);
            if (scalar / v3Lines[i].length() >= 0 &&
                scalar / v3Lines[i].length() <= 1 &&
                minDistanceOnLine > PX.length()) {
                this.nearestPoint = i;
                minDistanceOnLine = PX.length();
            }
            else {
                if (minDistanceOffLine > PX.length() && minDistanceOnLine == Infinity) {
                    this.nearestPoint = i;
                    minDistanceOffLine = PX.length();
                }
            }
            if (is2dfeature)
                pos.Height = X.z;
            orthogonals.push(pos.copy());
            orthogonals[i].Easting = X.x;
            orthogonals[i].Northing = X.y;
            orthogonals[i].Height = X.z;
        }
        return orthogonals;
    }
    /**
     * Abstract method for internal initialization
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        return;
    }
    /**
     * Abstract method for internal disabling
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        return;
    }
    /**
     * Abstract method for resetting the tool
     * @method reset
     * @param {?Object} evt evt is passed, if the reset is used as a cbf
     */
    reset(evt) {
        return;
    }
    destroy() {
        return;
    }
    getType() {
        return this.type;
    }
    getName() {
        return this.name;
    }
    getGroup() {
        return this.group;
    }
}
//# sourceMappingURL=tool.js.map